import { useState } from 'react';
import { formatFechaJornada } from '../../utils/fechaUtils';
import { Container, Row, Col, Card, Button, Form, Spinner, Badge, Alert } from 'react-bootstrap';
import { FaArrowLeft, FaUser, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../services/api';
import { toast } from 'react-toastify';
import './InscripcionManagement.css';

const ConsumirJornada = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const torneoNombreParam = searchParams.get('torneoNombre') || '';
  const torneoIdParam = searchParams.get('torneoId') || '';

  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [futbolistas, setFutbolistas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFutbolista, setSelectedFutbolista] = useState(null);
  const [jornadasDisputadas, setJornadasDisputadas] = useState([]);
  const [selectedJornada, setSelectedJornada] = useState(null);
  const [inscripcionInfo, setInscripcionInfo] = useState(null);
  const [loadingJornadas, setLoadingJornadas] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Función para buscar futbolistas (filtrado por torneo y saldo > 0)
  const handleSearch = async (term) => {
    setSearchTerm(term);

    if (term.length < 4) {
      setFutbolistas([]);
      return;
    }

    if (!torneoIdParam) {
      toast.error('No se ha especificado el torneo');
      return;
    }

    setLoading(true);
    try {
      const response = await API.get('/admin/consumir-jornada/buscar-futbolistas', {
        params: { torneoId: torneoIdParam, search: term }
      });
      const result = response.data;
      if (result.success) {
        setFutbolistas(result.data);
      } else {
        setFutbolistas([]);
      }
    } catch (error) {
      console.error('Error buscando futbolistas:', error);
      toast.error('Error al buscar futbolistas');
    } finally {
      setLoading(false);
    }
  };

  // Función para seleccionar futbolista y cargar jornadas disputadas
  const handleSelectFutbolista = async (futbolista) => {
    setSelectedFutbolista(futbolista);
    setSelectedJornada(null);
    setPreviewData(null);
    setLoadingJornadas(true);

    try {
      const response = await API.get(`/admin/consumir-jornada/${futbolista.id}/jornadas-disputadas`, {
        params: { torneoId: torneoIdParam }
      });

      const result = response.data;
      console.log('📊 Respuesta jornadas-disputadas:', result);

      if (result.success) {
        console.log('✅ Jornadas:', result.data.jornadas);
        console.log('✅ Inscripción:', result.data.inscripcion);

        setJornadasDisputadas(result.data.jornadas);
        setInscripcionInfo(result.data.inscripcion);
        setLoadingJornadas(false);

        // Seleccionar automáticamente la última jornada disputada
        if (result.data.jornadas.length > 0) {
          const ultimaJornada = result.data.jornadas[result.data.jornadas.length - 1];
          console.log('🎯 Auto-seleccionando jornada:', ultimaJornada);
          handleSelectJornada(ultimaJornada, futbolista.id);
        }
      } else {
        console.error('❌ Error en respuesta:', result);
        toast.error(result.message || 'Error al cargar jornadas');
        setSelectedFutbolista(null);
        setLoadingJornadas(false);
      }
    } catch (error) {
      console.error('Error cargando jornadas:', error);
      toast.error('Error al cargar información');
      setSelectedFutbolista(null);
      setLoadingJornadas(false);
    }
  };

  // Función para seleccionar jornada y calcular preview
  const handleSelectJornada = async (jornada, futbolistaId = null) => {
    setSelectedJornada(jornada);

    // Usar el futbolistaId pasado como parámetro o el del estado
    const idFutbolista = futbolistaId || (selectedFutbolista ? selectedFutbolista.id : null);

    if (!idFutbolista) {
      console.error('No hay futbolista seleccionado');
      return;
    }

    try {
      const response = await API.get(`/admin/consumir-jornada/${idFutbolista}/preview`, {
        params: { jornadaId: jornada.id }
      });

      const result = response.data;
      console.log('📈 Respuesta preview:', result);

      if (result.success) {
        console.log('✅ Preview data:', result.data);
        setPreviewData(result.data);
      } else {
        console.error('❌ Error en preview:', result);
        toast.error(result.message || 'Error al calcular preview');
      }
    } catch (error) {
      console.error('Error calculando preview:', error);
      toast.error('Error al calcular cambios');
    }
  };

  // Función para confirmar consumir jornada
  const handleConfirmarConsumir = async () => {
    setProcessing(true);
    setShowConfirmation(false);

    try {
      const response = await API.post(`/admin/consumir-jornada/${selectedFutbolista.id}`, {
        jornadaId: selectedJornada.id
      });

      const result = response.data;

      if (result.success) {
        toast.success('✅ Jornada consumida exitosamente');
        toast.info(`📊 Nuevo saldo: ${result.data.nuevoSaldo} jornadas`);

        if (result.data.jornadasEliminadas > 0) {
          toast.warning(`🗑️ ${result.data.jornadasEliminadas} jornada(s) futura(s) eliminada(s)`);
        }

        // Limpiar selección
        setSelectedFutbolista(null);
        setSelectedJornada(null);
        setJornadasDisputadas([]);
        setInscripcionInfo(null);
        setPreviewData(null);
        setSearchTerm('');
        setFutbolistas([]);
      } else {
        toast.error(result.message || 'Error al consumir jornada');
      }
    } catch (error) {
      console.error('Error consumiendo jornada:', error);
      toast.error('Error de conexión');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AdminLayout
      title={`Consumir Jornada${torneoNombreParam ? ` — ${torneoNombreParam}` : ''}`}
      subtitle="Marcar futbolista como No Presentado y ajustar saldo"
    >
      <Container fluid className="inscripcion-management inscripcion-white-bg p-0">
        {/* Botón de navegación */}
        <Row className="mb-3">
          <Col>
            <Button variant="outline-secondary" onClick={() => navigate('/admin/matches')}>
              <FaArrowLeft className="me-2" />Volver a Torneos
            </Button>
          </Col>
        </Row>

        {/* Búsqueda de futbolistas */}
        <Row className="mb-4">
          <Col xs={12}>
            <div>
              <Form.Control
                type="text"
                placeholder="🔍 Buscar futbolista por nombre..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                disabled={loading || selectedFutbolista}
              />
              <small className="text-muted">
                💡 Escribe al menos 4 caracteres para buscar
              </small>
            </div>
          </Col>
        </Row>

        {/* Resultados de búsqueda */}
        {loading && (
          <Row className="mb-4">
            <Col className="text-center">
              <Spinner animation="border" />
              <p className="mt-2">Buscando futbolistas...</p>
            </Col>
          </Row>
        )}

        {!selectedFutbolista && futbolistas.length > 0 && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5>Resultados de búsqueda ({futbolistas.length})</h5>
                </Card.Header>
                <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {futbolistas.map((futbolista) => (
                    <Card
                      key={futbolista.id}
                      className="mb-2 cursor-pointer"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSelectFutbolista(futbolista)}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <FaUser className="me-2" />
                            <strong>{futbolista.nombre}</strong>
                            <Badge bg="success" className="ms-2">Saldo: {futbolista.saldo_jornadas}</Badge>
                          </div>
                          <Badge bg="primary">Seleccionar</Badge>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Futbolista seleccionado */}
        {selectedFutbolista && (
          <Row className="mb-4">
            <Col>
              <Card className="border-primary">
                <Card.Header className="bg-primary text-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <FaUser className="me-2" />
                      {selectedFutbolista.nombre}
                    </h5>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => {
                        setSelectedFutbolista(null);
                        setSelectedJornada(null);
                        setJornadasDisputadas([]);
                        setInscripcionInfo(null);
                        setPreviewData(null);
                      }}
                    >
                      Cambiar
                    </Button>
                  </div>
                </Card.Header>
                {inscripcionInfo && (
                  <Card.Body>
                    <p className="mb-2">
                      <strong>Torneo:</strong> {inscripcionInfo.torneo_nombre}
                    </p>
                    <p className="mb-0">
                      <strong>Saldo actual:</strong> {inscripcionInfo.saldo_jornadas} jornadas
                    </p>
                  </Card.Body>
                )}
              </Card>
            </Col>
          </Row>
        )}

        {/* Selección de jornada */}
        {selectedFutbolista && loadingJornadas && (
          <Row className="mb-4">
            <Col className="text-center">
              <Spinner animation="border" />
              <p className="mt-2">Cargando jornadas disputadas...</p>
            </Col>
          </Row>
        )}

        {selectedFutbolista && !loadingJornadas && jornadasDisputadas.length === 0 && (
          <Row className="mb-4">
            <Col>
              <Alert variant="warning">
                <FaExclamationTriangle className="me-2" />
                No hay jornadas disputadas para este futbolista
              </Alert>
            </Col>
          </Row>
        )}

        {selectedFutbolista && jornadasDisputadas.length > 0 && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5>Seleccionar Jornada Disputada</h5>
                  <small className="text-muted">
                    Por defecto se selecciona la última jornada disputada
                  </small>
                </Card.Header>
                <Card.Body>
                  <Form.Group>
                    <Form.Label>Jornada:</Form.Label>
                    <Form.Select
                      value={selectedJornada?.id || ''}
                      onChange={(e) => {
                        const jornada = jornadasDisputadas.find(j => j.id === parseInt(e.target.value));
                        if (jornada) handleSelectJornada(jornada);
                      }}
                    >
                      {jornadasDisputadas.map((jornada) => (
                        <option key={jornada.id} value={jornada.id}>
                          Jornada {jornada.numero} - {formatFechaJornada(jornada.fecha)}
                          {jornada.estado_actual === 'No Presentado' ? ' (Ya marcada como No Presentado)' : ''}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Preview de cambios */}
        {previewData && (
          <Row className="mb-4">
            <Col>
              <Card className="border-warning">
                <Card.Header className="bg-warning">
                  <h5 className="mb-0">
                    <FaExclamationTriangle className="me-2" />
                    Vista Previa de Cambios
                  </h5>
                </Card.Header>
                <Card.Body>
                  <p className="mb-3">
                    <strong>Jornada seleccionada:</strong> Jornada {selectedJornada.numero} - {new Date(selectedJornada.fecha).toLocaleDateString('es-ES')}
                  </p>

                  <p className="mb-2">
                    <strong>Saldo actual:</strong> {previewData.saldoActual} → <strong>Nuevo saldo:</strong> {previewData.nuevoSaldo}
                  </p>

                  {previewData.jornadasFuturasActuales > 0 && (
                    <p className="mb-2">
                      <strong>Jornadas futuras inscritas:</strong> {previewData.jornadasFuturasActuales}
                    </p>
                  )}

                  {previewData.jornadasAEliminar > 0 && (
                    <div className="mb-2">
                      <p className="mb-1 text-danger">
                        <strong>⚠️ Se eliminarán {previewData.jornadasAEliminar} jornada(s) futura(s):</strong>
                      </p>
                      <ul className="mb-0">
                        {previewData.jornadasEliminadasDetalle.map((j, idx) => (
                          <li key={idx}>
                            Jornada {j.numero} - {new Date(j.fecha).toLocaleDateString('es-ES')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {previewData.nuevoSaldo === 0 && previewData.jornadasFuturasActuales > 0 && (
                    <p className="mb-0 text-danger">
                      <strong>⚠️ ATENCIÓN:</strong> El saldo quedará en 0. Se eliminarán TODAS las jornadas futuras.
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Confirmación inline */}
        {selectedJornada && previewData && !showConfirmation && (
          <Row className="mb-4">
            <Col className="text-center">
              <Button
                variant="warning"
                size="lg"
                onClick={() => setShowConfirmation(true)}
                disabled={processing}
              >
                <FaExclamationTriangle className="me-2" />
                Consumir Jornada
              </Button>
            </Col>
          </Row>
        )}

        {/* Confirmación expandida */}
        {showConfirmation && selectedJornada && previewData && (
          <Row className="mb-4">
            <Col>
              <Card className="border-danger">
                <Card.Header className="bg-danger text-white">
                  <h5 className="mb-0">
                    <FaExclamationTriangle className="me-2" />
                    Confirmar Consumir Jornada
                  </h5>
                </Card.Header>
                <Card.Body>
                  <p className="mb-2">
                    <strong>¿Estás seguro de que quieres consumir esta jornada?</strong>
                  </p>
                  <p className="mb-2">
                    <strong>Futbolista:</strong> {selectedFutbolista?.nombre}
                  </p>
                  <p className="mb-2">
                    <strong>Jornada:</strong> Jornada {selectedJornada?.numero} - {new Date(selectedJornada.fecha).toLocaleDateString('es-ES')}
                  </p>
                  <div className="mb-3">
                    <p className="mb-1"><strong>Esta acción:</strong></p>
                    <ul className="mb-0">
                      <li>Marcará la jornada como &quot;No Presentado&quot;</li>
                      <li>Restará 1 jornada del saldo</li>
                      {previewData?.jornadasAEliminar > 0 && (
                        <li className="text-danger">
                          <strong>Eliminará {previewData.jornadasAEliminar} jornada(s) futura(s)</strong>
                        </li>
                      )}
                    </ul>
                  </div>
                  <div className="d-flex gap-2 justify-content-end">
                    <Button
                      variant="secondary"
                      onClick={() => setShowConfirmation(false)}
                      disabled={processing}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleConfirmarConsumir}
                      disabled={processing}
                    >
                      {processing ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <FaCheckCircle className="me-2" />
                          Confirmar
                        </>
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </AdminLayout>
  );
};

export default ConsumirJornada;

